import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import { AlertTriangle, CheckCircle2, FileText, Plus, Edit, Trash2, Save } from 'lucide-react'
import BRDUploader from '../creative-matrix/BRDUploader'
import RuleTestsPanel from '../RuleTestsPanel'
import CurrentUserDisplay from '../CurrentUserDisplay'
import { apiService } from '@/services/api'
import { 
  SECTIONS_LIST, 
  FIELDS_LIST, 
  SEVERITY_LIST
} from '@/config/ruleSchema'

/**
 * COMPONENT SPECIFICATION: RulesManager
 * 
 * PURPOSE: 
 * - Manage user-level BRD rules and compliance requirements
 * - Allow editing of extracted rules from BRD documents
 * - Provide CRUD operations for pharmaceutical compliance rules
 * 
 * FEATURES:
 * - Upload BRD document to extract rules
 * - View all active rules with severity levels
 * - Edit existing rules (description, severity, field mapping)
 * - Add custom rules manually
 * - Delete or disable rules
 * - Rules are saved at user account level, not campaign level
 * 
 * INTEGRATION:
 * - Uses backend /api/brd/parse for BRD document processing
 * - Stores rules via UserDataService for persistence
 * - Rules apply globally to all user campaigns
 * 
 * DEPENDENCIES:
 * - BRDUploader component for document upload
 * - API endpoints: /api/user-data/brd-rules (GET, POST)
 */

interface RuleTest {
  input: string
  expected: 'pass' | 'fail' | 'warning' | 'info'
  description: string
}

interface Rule {
  id: string | number
  rule: string
  severity: 'critical' | 'warning' | 'minor'
  field: string
  section: string
  description: string
  enabled: boolean
  isCustom?: boolean
  channel?: string | string[]
  tests?: RuleTest[]
}

export default function RulesManager() {
  const [rules, setRules] = useState<Rule[]>([])
  const [showBRDUploader, setShowBRDUploader] = useState(false)
  const [editingRule, setEditingRule] = useState<Rule | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [filterSeverity, setFilterSeverity] = useState<string>('all')

  // Load existing rules on component mount
  useEffect(() => {
    loadRules()
  }, [])

  const loadRules = async () => {
    try {
      setIsLoading(true)
      const response = await apiService.request('/api/user-data/brd-rules')
      if (response.success) {
        setRules(response.data?.rules || [])
      }
    } catch (error) {
      console.error('Failed to load rules:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const saveRules = async (updatedRules: Rule[]) => {
    try {
      const response = await apiService.request('/api/user-data/brd-rules', {
        method: 'POST',
        body: JSON.stringify({ rules: updatedRules })
      })
      
      if (response.success) {
        setRules(updatedRules)
        console.log('Rules saved successfully')
      }
    } catch (error) {
      console.error('Failed to save rules:', error)
    }
  }

  const handleBRDProcessed = (newRules: any[]) => {
    const processedRules: Rule[] = newRules.map(rule => ({
      id: `rule-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      rule: rule.rule,
      severity: rule.severity,
      field: rule.field,
      section: rule.section,
      description: rule.description,
      enabled: true,
      isCustom: false
    }))
    
    const updatedRules = [...rules, ...processedRules]
    saveRules(updatedRules)
  }

  const addCustomRule = () => {
    const newRule: Rule = {
      id: `custom-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      rule: 'New Custom Rule',
      severity: 'warning',
      field: 'customField',
      section: 'Custom Rules',
      description: 'Enter rule description',
      enabled: true,
      isCustom: true
    }
    setEditingRule(newRule)
  }

  const updateRule = (updatedRule: Rule) => {
    const updatedRules = rules.find(r => r.id === updatedRule.id)
      ? rules.map(rule => rule.id === updatedRule.id ? updatedRule : rule)
      : [...rules, updatedRule]
    
    saveRules(updatedRules)
    setEditingRule(null)
  }

  const deleteRule = (ruleId: string) => {
    const updatedRules = rules.filter(rule => rule.id !== ruleId)
    saveRules(updatedRules)
  }

  const toggleRule = (ruleId: string | number) => {
    const updatedRules = rules.map(rule => 
      rule.id === ruleId ? { ...rule, enabled: !rule.enabled } : rule
    )
    saveRules(updatedRules)
  }

  const updateRuleTests = (ruleId: number | string, tests: RuleTest[]) => {
    const updatedRules = rules.map(rule =>
      rule.id === ruleId ? { ...rule, tests } : rule
    )
    saveRules(updatedRules)
  }

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <AlertTriangle className="w-4 h-4 text-red-500" />
      case 'warning':
        return <AlertTriangle className="w-4 h-4 text-yellow-500" />
      case 'minor':
        return <CheckCircle2 className="w-4 h-4 text-blue-500" />
      default:
        return <CheckCircle2 className="w-4 h-4 text-gray-500" />
    }
  }

  const filteredRules = rules.filter(rule => {
    const matchesSearch = rule.rule.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         rule.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         rule.section.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesSeverity = filterSeverity === 'all' || rule.severity === filterSeverity
    return matchesSearch && matchesSeverity
  })

  const ruleStats = {
    total: rules.length,
    enabled: rules.filter(r => r.enabled).length,
    critical: rules.filter(r => r.severity === 'critical' && r.enabled).length,
    warning: rules.filter(r => r.severity === 'warning' && r.enabled).length,
    minor: rules.filter(r => r.severity === 'minor' && r.enabled).length
  }

  return (
    <div className="space-y-6">
      {/* Current User Display */}
      <CurrentUserDisplay />

      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              BRD Rules Management
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={() => setShowBRDUploader(true)} className="gap-2">
                <FileText className="w-4 h-4" />
                Upload New BRD
              </Button>
              <Button variant="outline" onClick={addCustomRule} className="gap-2">
                <Plus className="w-4 h-4" />
                Add Custom Rule
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{ruleStats.total}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Total Rules</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{ruleStats.enabled}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Enabled</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">{ruleStats.critical}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Critical</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">{ruleStats.warning}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Warning</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{ruleStats.minor}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Minor</div>
            </div>
          </div>

          {/* Filters */}
          <div className="flex gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search rules..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <Select value={filterSeverity} onValueChange={setFilterSeverity}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
                <SelectItem value="warning">Warning</SelectItem>
                <SelectItem value="minor">Minor</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Rules List */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center">
              <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p>Loading rules...</p>
            </div>
          ) : filteredRules.length === 0 ? (
            <div className="p-8 text-center">
              <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No BRD Rules Found
              </h3>
              <p className="text-gray-500 dark:text-gray-400 mb-4">
                Upload a Business Requirements Document to extract compliance rules.
              </p>
              <Button onClick={() => setShowBRDUploader(true)}>
                Upload BRD Document
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredRules.map((rule) => (
                <div key={rule.id} className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4 flex-1">
                      <div className="flex-shrink-0 mt-1">
                        {getSeverityIcon(rule.severity)}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                            {rule.rule}
                          </h4>
                          <Badge variant={
                            rule.severity === 'critical' ? 'destructive' :
                            rule.severity === 'warning' ? 'secondary' : 'outline'
                          }>
                            {rule.severity}
                          </Badge>
                          {rule.isCustom && <Badge variant="outline">Custom</Badge>}
                          {!rule.enabled && <Badge variant="secondary">Disabled</Badge>}
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                          {rule.description}
                        </p>
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <span>Section: {rule.section}</span>
                          <span>Field: {rule.field}</span>
                        </div>
                        
                        {/* Test Cases Panel */}
                        <RuleTestsPanel 
                          rule={rule as any} 
                          onUpdateTests={updateRuleTests}
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleRule(rule.id)}
                        className={rule.enabled ? 'text-green-600' : 'text-gray-400'}
                      >
                        {rule.enabled ? 'Enabled' : 'Disabled'}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditingRule(rule)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteRule(rule.id)}
                        className="text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Rule Modal */}
      {editingRule && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl">
            <h3 className="text-lg font-semibold mb-4">
              {rules.find(r => r.id === editingRule.id) ? 'Edit Rule' : 'Add Custom Rule'}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Rule Title</label>
                <Input
                  value={editingRule.rule}
                  onChange={(e) => setEditingRule({...editingRule, rule: e.target.value})}
                  placeholder="Enter rule title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <Textarea
                  value={editingRule.description}
                  onChange={(e) => setEditingRule({...editingRule, description: e.target.value})}
                  placeholder="Enter detailed rule description"
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Severity</label>
                  <Select
                    value={editingRule.severity}
                    onValueChange={(value) => setEditingRule({...editingRule, severity: value as any})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SEVERITY_LIST.map((severity) => (
                        <SelectItem key={severity.value} value={severity.value}>
                          <span className="flex items-center gap-2">
                            {severity.label}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Section</label>
                  <Select
                    value={editingRule.section}
                    onValueChange={(value) => setEditingRule({...editingRule, section: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select section" />
                    </SelectTrigger>
                    <SelectContent>
                      {SECTIONS_LIST.map((section) => (
                        <SelectItem key={section.value} value={section.value}>
                          {section.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Field</label>
                  <Select
                    value={editingRule.field}
                    onValueChange={(value) => setEditingRule({...editingRule, field: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select field" />
                    </SelectTrigger>
                    <SelectContent>
                      {FIELDS_LIST.map((field) => (
                        <SelectItem key={field.value} value={field.value}>
                          <div className="flex flex-col">
                            <span>{field.label}</span>
                            <span className="text-xs text-gray-500">{field.description}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={() => setEditingRule(null)}>
                Cancel
              </Button>
              <Button onClick={() => updateRule(editingRule)} className="gap-2">
                <Save className="w-4 h-4" />
                Save Rule
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* BRD Uploader */}
      <BRDUploader
        isOpen={showBRDUploader}
        onClose={() => setShowBRDUploader(false)}
        onBRDProcessed={handleBRDProcessed}
      />
    </div>
  )
}
